import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { api, supabase } from '../api/api';
import { useAuth } from '../contexts/AuthContext';
import NotificationItem from './NotificationItem';
import toast from 'react-hot-toast';

/**
 * Header bell + unread badge + dropdown drawer. Outside-click-to-close follows the
 * same ref + mousedown pattern as MultiSelect's dropdown (the app's one other
 * inline popover) rather than inventing a new interaction.
 */
const NotificationBell = () => {
    const { session } = useAuth();
    const userId = session?.user?.id;
    const [isOpen, setIsOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [confirmingClearAll, setConfirmingClearAll] = useState(false);
    const containerRef = useRef(null);
    // Ids this drawer deleted itself - their Realtime DELETE echo is skipped so the badge
    // isn't decremented a second time for the same row.
    const locallyDismissedIds = useRef(new Set());

    const fetchUnreadCount = useCallback(async () => {
        try {
            const { count } = await api.getUnreadNotificationCount();
            setUnreadCount(count || 0);
        } catch (error) {
            console.error('Failed to fetch unread notification count:', error);
        }
    }, []);

    // 60s poll stays as a correctness backstop (catches anything missed if the realtime
    // socket drops); the Realtime subscription below is what makes the badge move the
    // instant a notification is actually inserted, rather than waiting on this interval.
    useEffect(() => {
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 60000);
        return () => clearInterval(interval);
    }, [fetchUnreadCount]);

    // Live updates: a new row for this user bumps the badge and (if the drawer happens to
    // be open) slots straight into the list, with no reload/poll-interval delay. A DELETE
    // (an admin unsending a broadcast) removes it from the open list and un-counts it if
    // it was unread - REPLICA IDENTITY FULL on notifications is what makes the
    // recipient_id filter still match on a DELETE's "old row". RLS already scopes what
    // Realtime will deliver to auth.uid() = recipient_id; the filter here just avoids
    // subscribing to other users' rows at all.
    useEffect(() => {
        if (!userId) return undefined;

        const channel = supabase
            .channel(`notifications-bell-${userId}`)
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'notifications',
                filter: `recipient_id=eq.${userId}`,
            }, (payload) => {
                setUnreadCount(prev => prev + 1);
                setNotifications(prev => [payload.new, ...prev].slice(0, 10));
            })
            .on('postgres_changes', {
                event: 'DELETE',
                schema: 'public',
                table: 'notifications',
                filter: `recipient_id=eq.${userId}`,
            }, (payload) => {
                const removedId = payload.old?.id;
                if (!removedId) return;
                if (locallyDismissedIds.current.delete(removedId)) return;
                setNotifications(prev => prev.filter(n => n.id !== removedId));
                if (!payload.old.read_at) {
                    setUnreadCount(prev => Math.max(0, prev - 1));
                }
            })
            .subscribe((status, err) => {
                if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
                    console.error('Notifications realtime channel issue:', status, err);
                }
            });

        return () => {
            supabase.removeChannel(channel);
        };
    }, [userId]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleToggle = async () => {
        const nextOpen = !isOpen;
        setIsOpen(nextOpen);
        setConfirmingClearAll(false);
        if (nextOpen) {
            setIsLoading(true);
            try {
                const data = await api.getNotifications({ limit: 10 });
                setNotifications(data || []);
            } catch (error) {
                console.error('Failed to fetch notifications:', error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await api.markAllNotificationsRead();
            setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Failed to mark all notifications read:', error);
        }
    };

    const handleDismiss = async (notification) => {
        locallyDismissedIds.current.add(notification.id);
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
        if (!notification.read_at) {
            setUnreadCount(prev => Math.max(0, prev - 1));
        }
        try {
            await api.dismissNotification(notification.id);
        } catch (error) {
            locallyDismissedIds.current.delete(notification.id);
            console.error('Failed to dismiss notification:', error);
            toast.error('Failed to dismiss notification.');
            // Resync rather than trying to splice it back into its old position.
            const data = await api.getNotifications({ limit: 10 }).catch(() => null);
            if (data) setNotifications(data);
            fetchUnreadCount();
        }
    };

    const handleClearAll = async () => {
        setConfirmingClearAll(false);
        notifications.forEach(n => locallyDismissedIds.current.add(n.id));
        setNotifications([]);
        setUnreadCount(0);
        try {
            await api.clearAllNotifications();
        } catch (error) {
            console.error('Failed to clear notifications:', error);
            toast.error('Failed to clear notifications.');
            const data = await api.getNotifications({ limit: 10 }).catch(() => null);
            if (data) setNotifications(data);
            fetchUnreadCount();
        }
    };

    const handleOpenNotification = async (notification) => {
        setIsOpen(false);
        if (!notification.read_at) {
            setUnreadCount(prev => Math.max(0, prev - 1));
            try {
                await api.markNotificationRead(notification.id);
            } catch (error) {
                console.error('Failed to mark notification read:', error);
            }
        }
    };

    return (
        <div className="relative" ref={containerRef}>
            <button
                onClick={handleToggle}
                className="relative flex items-center p-2 rounded-md text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                aria-label="Notifications"
            >
                <Bell size={18} />
                {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-96 max-w-[90vw] bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-50 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
                        <span className="text-sm font-bold text-white">Notifications</span>
                        {confirmingClearAll ? (
                            <div className="flex items-center gap-3 text-xs font-semibold">
                                <span className="text-gray-400">Clear all?</span>
                                <button onClick={handleClearAll} className="text-red-400 hover:text-red-300">Yes</button>
                                <button onClick={() => setConfirmingClearAll(false)} className="text-gray-400 hover:text-white">No</button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-3">
                                <button onClick={handleMarkAllRead} className="text-xs font-semibold text-amber-400 hover:text-amber-300">
                                    Mark all read
                                </button>
                                {notifications.length > 0 && (
                                    <button onClick={() => setConfirmingClearAll(true)} className="text-xs font-semibold text-gray-400 hover:text-white">
                                        Clear all
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {isLoading ? (
                            <div className="px-4 py-6 text-center text-sm text-gray-500">Loading...</div>
                        ) : notifications.length === 0 ? (
                            <div className="px-4 py-6 text-center text-sm text-gray-500">You're all caught up.</div>
                        ) : (
                            notifications.map(n => (
                                <NotificationItem key={n.id} notification={n} onOpen={handleOpenNotification} onDismiss={handleDismiss} />
                            ))
                        )}
                    </div>
                    <Link
                        to="/notifications"
                        onClick={() => setIsOpen(false)}
                        className="block text-center py-3 text-xs font-semibold text-amber-400 hover:text-amber-300 border-t border-gray-700"
                    >
                        View all notifications
                    </Link>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
